"use client";

import React, { useEffect, useMemo, useState } from "react";
import { TableColumnsType } from "antd";
import { createStyles } from "antd-style";
import {
  VirtualTableView,
  Icon,
  LoadingLottie,
  ospThemes,
} from "@ospweb/sdk-ui";
import uniq from "lodash-es/uniq";
import { cn } from "utils";
import type { HoldingChange } from "../types";
import { getChangeTypeText } from "../utils";
import { HoldingTableCellAsset } from "../HoldingTableCellAsset";
import { useHoldingChangeTable } from "./useHoldingChangeTable";
import Translations from "@translate/components/WhaleHoldings/WhaleHoldingsDetail/HoldingChangeTable";
import { I18nUtil } from "@utils/i18n";
import { useStyles } from "@/src/components/table/styles";
import { useGoToDetail } from "@hooks";
import { useIsPc } from "@hooks/useIsPc";
import { Locale } from "@utils/i18n.locale";
import { EmptyState } from "@/src/components/Empty";

const I18n = I18nUtil.createScoped(Translations);

/** 禁用行 class，无 asset.id 时不可点击、无手型与 hover 背景 */
const ROW_DISABLED_CLASS = "whale-holding-row-disabled";

/** 本组件与 HoldingListTable 共用表格样式 */
const useLocalStyles = createStyles(({ css }) => ({
  tablePaddingNone: css`
    & .ant-table {
      padding: 0 !important;
    }
    & .ant-table table::after {
      display: none !important;
    }
    /* 隐藏 thead 上的 ::after，避免影响表头样式 */
    & .ant-table-thead::after {
      display: none !important;
    }
    /* 禁用行：覆盖 tableRowCursor，无手型、无 hover 背景 */
    & .ant-table-tbody > tr.${ROW_DISABLED_CLASS} {
      cursor: default !important;
    }
    & .ant-table-tbody > tr.${ROW_DISABLED_CLASS} .ant-table-cell-row-hover {
      background-color: ${
        ospThemes.colors.background_solid_secondary
      } !important;
    }
    /* 表格行样式 */
    & .ant-table-tbody > tr > td {
      height: 52px;
      padding: 8px;
    }
  `,
}));

interface HoldingChangeTableProps {
  /** CIK ID */
  cikId: string;
  /** 最新申报时间 */
  lastReportDate?: string;
  /** 自定义样式类名 */
  className?: string;
  /** 由页面传入的批量查询收藏状态，与 HoldingListTable 共用 */
  checkFavoritesStatus?: (ids: string[]) => void;
  /** 表头吸顶时距离视口顶部的距离（与详情页导航栏一致，不传则不吸顶） */
  stickyOffset?: number;
}

/**
 * 持仓变动表格组件
 * - 使用与 HoldingListTable 相同的 VirtualTableView，Asset 列宽一致便于对齐
 * - 数据通过 useHoldingChangeTable 获取，第一页 5 条，点击「View Full History」每次加载 10 条（不做滚动加载）
 * - 仅三列，无横向滚动
 */
export const HoldingChangeTable: React.FC<HoldingChangeTableProps> = ({
  cikId,
  lastReportDate,
  className,
  checkFavoritesStatus,
  stickyOffset,
}) => {
  const { styles, cx } = useStyles();
  const { styles: localStyles } = useLocalStyles();
  const { onDetail, isLoading: isLoadingDetail } = useGoToDetail("stock");
  const [activeRow, setActiveRow] = useState<number | null>(null);
  const [isPC] = useIsPc();
  const locale = I18nUtil.getCurrentLocale() as Locale;
  const {
    changes,
    isLoading,
    hasLoadedOnce,
    isLoadingMore,
    hasMore,
    handleLoadMore,
  } = useHoldingChangeTable({
    cikId,
    enabled: Boolean(cikId),
  });
  const hasData = changes.length > 0;

  useEffect(() => {
    if (!checkFavoritesStatus) {
      return;
    }
    const ids = uniq(
      changes.map((c) => c.asset.id).filter((id): id is string => Boolean(id)),
    );
    if (ids.length) {
      checkFavoritesStatus(ids);
    }
  }, [changes, checkFavoritesStatus]);

  const columns: TableColumnsType<HoldingChange> = useMemo(
    () => [
      {
        title: I18n.t("Asset"),
        dataIndex: "asset",
        key: "asset",
        fixed: "left",
        width: isPC ? 232 : 150,
        align: "left",
        render: (_: unknown, record: HoldingChange) => (
          <HoldingTableCellAsset
            asset={record.asset}
            textClassName="line-clamp-1 md:w-40 w-auto"
          />
        ),
      },
      {
        title: I18n.t("Chg(Shares)"),
        dataIndex: "changeShares",
        key: "changeShares",
        align: "left",
        minWidth: 80,
        render: (_: unknown, record: HoldingChange) => (
          <span className="body_bold_m text-content_primary">
            {record.changeSharesDisplay}
          </span>
        ),
      },
      {
        title: I18n.t("Action"),
        dataIndex: "changeType",
        key: "changeType",
        align: "left",
        minWidth: 80,
        render: (_: unknown, record: HoldingChange) => (
          <span className="body_bold_m text-content_primary">
            {getChangeTypeText(record.changeType, locale)}
          </span>
        ),
      },
    ],
    [isPC, locale],
  );

  const dataSource = useMemo(
    () =>
      changes.map((item, index) => ({
        ...item,
        key: `${item.asset.id}-${index}`,
      })),
    [changes],
  );

  return (
    <div className={cn("flex flex-col gap-3 md:gap-1 px-4 md:px-0", className)}>
      <div
        className={cn(
          "flex items-center gap-2 justify-between md:justify-start",
        )}
      >
        <h3
          aria-label="Holding Change Table h3"
          data-openreplay-label="Holding Change Table h3 Semantic"
          id="sem-holding-change-table-h3"
          className="title_xl text-content_primary"
        >
          {I18n.t("Latest Moves")}
        </h3>
        <span className="body_s text-content_tertiary">
          {I18n.t("Latest Filing:")}
          {lastReportDate}
        </span>
      </div>
      {/* 1）首屏首次加载：用固定高度 304 的 loading 占位，不走空状态、不渲染表格结构 */}
      {!hasLoadedOnce && isLoading ? (
        <div
          className="flex w-full items-center justify-center min-h-[304px]"
          aria-busy="true"
          aria-label="Loading latest moves"
        >
          <LoadingLottie style={{ width: 56, height: 56 }} />
        </div>
      ) : hasLoadedOnce && !isLoading && !hasData ? (
        /* 2）已加载完成且确实没有数据：显示固定高度 304 的空状态 */
        <div className="flex w-full items-center justify-center min-h-[304px]">
          <EmptyState hideDescription={true} />
        </div>
      ) : (
        /* 3）有数据时渲染表格，后续分页 loading 仍走 VirtualTableView 自带的 loadingMore 逻辑 */
        <>
          <VirtualTableView<HoldingChange>
            sticky={
              stickyOffset != null ? { offsetHeader: stickyOffset } : undefined
            }
            columns={columns.map((col) => ({
              ...col,
              title:
                typeof col.title === "string" ? (
                  <h3 className="body_medium_s text-content_secondary">
                    {col.title}
                  </h3>
                ) : (
                  col.title
                ),
            }))}
            dataSource={dataSource}
            rowHeight={52}
            loading={isLoading}
            pagination={false}
            enableInfiniteScroll={false}
            className={cx(
              styles.tableCustom,
              styles.tableCustomNoBorder,
              styles.tableRowCursor,
              localStyles.tablePaddingNone,
            )}
            onRow={(record, index) => ({
              onClick: () => {
                if (!record.asset?.id) {
                  return;
                }
                setActiveRow(index ?? null);
                onDetail?.(record.asset);
              },
              onMouseLeave: () => {
                if (index === activeRow) {
                  setActiveRow(null);
                }
              },
              className: cn(
                activeRow === index &&
                  isLoadingDetail &&
                  "opacity-50 cursor-progress",
                record.asset?.id ? "cursor-pointer" : ROW_DISABLED_CLASS,
              ),
            })}
          />
          {hasMore && (
            <button
              aria-label="Holding Change Table"
              data-openreplay-label="Holding Change Table Button"
              id="btn-holding-change-table"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              className="flex items-center justify-center gap-0.5 w-full pt-3 disabled:opacity-50"
            >
              {isLoadingMore ? (
                <span className="button_s text-content_tertiary">
                  {I18n.t("Loading...")}
                </span>
              ) : (
                <>
                  <span className="button_s text-content_tertiary">
                    {I18n.t("View Full History")}
                  </span>
                  <Icon
                    name="chevron-down"
                    size={16}
                    className="text-content_tertiary"
                  />
                </>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
};
